import { EventAggregator, inject, observable, resolve } from 'aurelia';
import { IRouter, IRouteViewModel, Params, route, RouteNode } from '@aurelia/router-lite';
import { IStore } from '@aurelia/state';
import { apiEndpoint, getAWSBucketEndpoint, websiteShortCode } from '../../../environment';
import { DEVICES } from '../../../resources/constants';
import { BlogAuthorService } from '../../../services/blog-author-service';
import { BlogService } from '../../../services/blog-service';
import { WebsiteService } from '../../../services/website-service';
import { ActionNames, UpdateMetaDescription, UpdateMetaTitle } from '../../../state';
import { NameFromShortcodeValueConverter } from '../../../resources/value-converters';

import './author.scss';

@route({
    path: ['blog/author/:author'],
    title: 'Author',
})
@inject(WebsiteService, BlogService, BlogAuthorService, IRouter, EventAggregator, IStore, NameFromShortcodeValueConverter)
export class Author implements IRouteViewModel {
    constructor(
        private websiteService: WebsiteService,
        private blogService: BlogService,
        private blogAuthorService: BlogAuthorService,
        private router: IRouter,
        private eventAggregator: EventAggregator,
        private store: IStore<{}, UpdateMetaTitle | UpdateMetaDescription> = resolve(IStore),
        private nameFromShortcodeValueConverter: NameFromShortcodeValueConverter) {
        this.baseAwsEndpoint = getAWSBucketEndpoint('blogs');
        this.DEVICES = DEVICES;
    }

    baseAwsEndpoint: string;
    blog;
    blogRoute;
    environment = apiEndpoint();
    relatedPost;
    blogs;
    count = 1;
    isAttached = false;
    @observable page = 1;
    startingIndex: number;
    perPage;
    endingIndex;
    authorInfo;
    blogsCount;
    width;
    sizeChanged;
    breadcrumbs: { title: string; href: string }[] = [];
    homeRoute;
    DEVICES;
    pageLoading: boolean = true;
    shortCode: string = websiteShortCode() ?? 'CG';
    skeletonPosts: any[] = Array.from({ length: 4 });
    currentRouteNode;

    async loading(params: Params, current: RouteNode) {
        this.authorInfo = await this.blogAuthorService.getByAlias(params.alias);
        if (!this.authorInfo) {
            this.router.load('404');
        }
        this.currentRouteNode = current;
    }

    detaching() {
        this.sizeChanged?.dispose();
    }

    async attached() {
        this.scrollToAuthor();
        this.isAttached = true;
        this.width = document.body.clientWidth;
        const blogAuthorTitle = `Author ${this.authorInfo.firstName}${this.authorInfo.lastName ? ` ${this.authorInfo.lastName}` : ''} - ${this.nameFromShortcodeValueConverter.toView(this.shortCode)}`;
        this.currentRouteNode.title = blogAuthorTitle;
        this.store.dispatch({ type: ActionNames.UPDATE_META_TITLE, value: blogAuthorTitle });
        this.store.dispatch({ type: ActionNames.UPDATE_META_DESCRIPTION, value: this.authorInfo.about });
        this.breadcrumbs = [
            { title: 'Blogs', href: '../../' },
            { title: `${this.authorInfo.firstName} ${this.authorInfo.lastName}`, href: `author/${this.authorInfo.alias}` },
        ];
        await this.pageByWidth();
        this.handleEventSubscriptions();
    }

    handleEventSubscriptions() {
        this.sizeChanged = this.eventAggregator.subscribe('size-changed', async payload => {
            this.width = payload.width;
            await this.pageByWidth();
        });
    }

    async pageByWidth() {
        if (this.width >= DEVICES.PHONE && this.width < DEVICES.DESKTOP) {
            this.perPage = 4;
        }
        else {
            this.perPage = 4;
        }
        await this.pageChanged();
        this.pageLoading = false;
        this.scrollToTop();
    }

    async tagClickHandler(tag) {
        this.router.load(`category/${tag.slug}`);
    }

    async pageChanged() {
        if (this.isAttached) {
            this.blogs = await this.blogService.filterBlogs(
                {
                    page: this.page,
                    pageSize: this.perPage,
                    published: true,
                    authorAlias: this.authorInfo.alias,
                    websites: [this.shortCode]
                }
            );
            this.relatedPost = this.blogs.blogPosts;
            this.blogsCount = this.blogs.totalCount;
            this.count = Math.ceil(this.blogsCount / this.perPage);
            if (this.page > this.count && this.count > 0) {
                this.page = this.count;
            }
        }
    }

    scrollToTop() {
        window.document.getElementById('navbar')?.scrollIntoView();
    }

    scrollToAuthor() {
        window.document.getElementById('author')?.scrollIntoView();
    }

    get hasAuthorSocialMedia(): boolean {
        return this.authorInfo.facebookUrl || this.authorInfo.instagramUrl || this.authorInfo.discordUrl;
    }

    get hasAuthorBio(): boolean {
        return this.authorInfo.bio;
    }

    handlePagination(value: CustomEvent): void {
        return this.page = value.detail.page;
    }
}
